/**
 * PATHWAY-4096 — bucket **(a)** legacy action names (not in {@link AmplitudeProductEvent}).
 *
 * Definitions:
 * - **(a)** Remove hand track; autocapture owns the interaction (Element / Screen / session).
 * - **(b)** Product events in `productEvents.ts` / `AMPLITUDE_PRODUCT_EVENT_REGISTRY`.
 *
 * See `packages/fe-shared/src/amplitude/README.md` → "Taxonomy Buckets: (a), (b)".
 *
 * @example Vitest — bucket (a) names must never become product event types
 * ```ts
 * import {
 *   AmplitudeProductEvent,
 *   LEGACY_ACTIONS_BUCKET_A,
 * } from '@cfa/pathway-fe-shared';
 *
 * it('autocapture-only legacy actions are not AmplitudeProductEvent values', () => {
 *   const productNames = new Set(Object.values(AmplitudeProductEvent));
 *   for (const action of LEGACY_ACTIONS_BUCKET_A) {
 *     expect(productNames.has(action as never)).toBe(false);
 *   }
 * });
 * ```
 */

/**
 * Legacy `UserAction.action` values superseded by **Element Interacted** (or similar).
 * Empty — remaining chrome lives in bucket **(b)** (`AmplitudeProductEvent`).
 */
export const LEGACY_ACTIONS_BUCKET_A = [] as const;

/**
 * Top-level native events (not `UserAction` + `action`) replaced by screen autocapture.
 *
 * @example Vitest — `trackAmpEvent('ViewScreen', …)` should be removed after `trackScreenView` migration
 * ```ts
 * import { LEGACY_TOP_LEVEL_BUCKET_A } from '@cfa/pathway-fe-shared';
 *
 * it('does not emit ViewScreen after Screen Viewed migration', () => {
 *   expect(capturedTopLevelEvents).not.toContain(LEGACY_TOP_LEVEL_BUCKET_A[0]);
 * });
 * ```
 */
export const LEGACY_TOP_LEVEL_BUCKET_A = ['ViewScreen'] as const;

export type LegacyActionBucketA = (typeof LEGACY_ACTIONS_BUCKET_A)[number];
